import { ApolloQueryResult } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";

import {
  CustomerFolderTypeCode,
  MessageThreadTitle,
  MessageThreadTopicCode,
  QuestionnaireTypeCode,
} from "src/core/constants/folder";
import { Status } from "src/core/constants/folders";
import {
  FolderFileQuery,
  useCreateMessageThreadMutation,
  useCreateQuestionnaireMutation,
} from "src/core/gql/graphql";

export const useFolderInitialization = (
  folderFile: FolderFileQuery | undefined,
  refetch: () => Promise<ApolloQueryResult<FolderFileQuery>>
) => {
  const [isInitializing, setIsInitializing] = useState(false);
  const [hasCreatedMessageThread, setHasCreatedMessageThread] = useState(false);
  const [hasCreatedQuestionnaire, setHasCreatedQuestionnaire] = useState(false);

  // Reset des states quand on change de dossier
  useEffect(() => {
    setIsInitializing(false);
    setHasCreatedMessageThread(false);
    setHasCreatedQuestionnaire(false);
  }, [folderFile?.folderFile?.id]);

  const [createQuestionnaire] = useCreateQuestionnaireMutation();
  const [createMessageThread] = useCreateMessageThreadMutation();

  const folderFileEntity = folderFile?.folderFile ?? null;

  const isPPH =
    folderFileEntity?.currentCustomerFolder?.customerFolderType?.code ===
    CustomerFolderTypeCode.PPH;

  useEffect(() => {
    // Pas de data (query pas encore revenue) -> no-op
    if (!folderFile) return;

    // Entity non trouvée -> no-op + stop init si jamais
    if (!folderFileEntity) {
      if (isInitializing) setIsInitializing(false);
      return;
    }

    const initializeFolder = async () => {
      if (
        !isInitializing &&
        folderFileEntity.fileCustomerFolder?.supportingQuestionnaires &&
        folderFileEntity.fileCustomerFolder.supportingQuestionnaires[0]?.questionnaire === null &&
        !hasCreatedQuestionnaire
      ) {
        setIsInitializing(true);

        try {
          await createQuestionnaire({
            variables: {
              questionnaireEntityId: folderFileEntity.fileCustomerFolder?.id,
              questionnaireTypeCode:
                folderFileEntity.currentCustomerFolder?.customerFolderType?.code ===
                CustomerFolderTypeCode.PPH
                  ? QuestionnaireTypeCode.LAB_PPH
                  : QuestionnaireTypeCode.LAB,
            },
          });

          setHasCreatedQuestionnaire(true);
          await refetch();
        } catch (error) {
          setHasCreatedQuestionnaire(true);
          console.error("Erreur lors création du questionnaire:", error);
        } finally {
          setIsInitializing(false);
        }
      }
    };

    const initializeMessageThread = async () => {
      if (
        folderFileEntity.id &&
        folderFileEntity.workflowStatus?.state?.code === Status.DRAFT &&
        !isInitializing &&
        !hasCreatedMessageThread &&
        folderFileEntity.messageThreads &&
        !folderFileEntity.messageThreads.some((mt) => mt.title === MessageThreadTitle.COMMENTS)
      ) {
        setIsInitializing(true);

        try {
          await createMessageThread({
            variables: {
              folderFileId: folderFileEntity.id,
              title: MessageThreadTitle.COMMENTS,
              topicCodes: [MessageThreadTopicCode.OTHER],
              discussantIds: folderFileEntity.adminStatus?.author?.id
                ? [folderFileEntity.adminStatus.author.id]
                : [],
            },
          });

          setHasCreatedMessageThread(true);
          await refetch();
        } catch (error) {
          setHasCreatedMessageThread(true);
          console.error("Erreur lors création du fil de discussion:", error);
        } finally {
          setIsInitializing(false);
        }
      }
    };

    void initializeFolder();
    void initializeMessageThread();
  }, [
    folderFile,
    folderFileEntity,
    createQuestionnaire,
    createMessageThread,
    refetch,
    isInitializing,
    hasCreatedMessageThread,
    hasCreatedQuestionnaire,
  ]);

  const questionnaireLAB = useMemo(() => {
    return folderFileEntity?.fileCustomerFolder?.supportingQuestionnaires?.find(
      (q) =>
        q.questionnaire?.type.code ===
        (isPPH ? QuestionnaireTypeCode.LAB_PPH : QuestionnaireTypeCode.LAB)
    );
  }, [folderFileEntity, isPPH]);

  const isQuestionnaireReady = Boolean(questionnaireLAB?.questionnaire);

  return { isInitializing, isQuestionnaireReady };
};
